import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FILE_INCORRECT } from "@/features/company/constants/excel";
import { TOption } from "@/types/common";
import dayjs from "dayjs";

const TEMPLATE_END_ROW = 201;

// Column widths matching the example template (indexed by column number 1-50)
const columnWidths: Record<number, number> = {
  1: 28, // A: 求職活動ステータス
  2: 22, // B: 紹介事業所名
  3: 14.625, // C: 人材担当者
  4: 21.375, // D: 氏名
  5: 21.375, // E: カナ表記
  6: 11.875, // F: 生年月日
  7: 9.125, // G: 呼び名
  8: 17.5, // H: 人材管理番号
  9: 9.5, // I: 国籍
  10: 6.75, // J: 性別
  11: 15, // K: 電話番号
  12: 25.375, // L: メールアドレス
  13: 34, // M: その他の連絡手段・SNS①
  14: 30, // N: その他の連絡先ID等①
  15: 34, // O: その他の連絡手段・SNS②
  16: 30, // P: その他の連絡先ID等②
  17: 34, // Q: その他の連絡手段・SNS③
  18: 30, // R: その他の連絡先ID等③
  19: 34, // S: その他の連絡手段・SNS④
  20: 30, // T: その他の連絡先ID等④
  21: 9.25, // U: 居住地
  22: 11.875, // V: 郵便番号
  23: 38, // W: 所在地（町名丁目番地号等）
  24: 30, // X: 本国における居住地
  25: 12.875, // Y: 在留資格
  26: 11.875, // Z: 在留期限
  27: 42, // AA: 特定技能1号起算日
  28: 35, // AB: ブランク期間（月単位）
  29: 18.75, // AC: パスポート番号
  30: 17.5, // AD: パスポート期限
  31: 17.25, // AE: 分野
  32: 30.625, // AF: 区分
  33: 52, // AG: 日本語能力証明書
  34: 32, // AH: 日本語能力（その他）
  35: 55, // AI: 特定技能合格証
  36: 12.125, // AJ: 職種
  37: 23.75, // AK: 作業
  38: 10.875, // AL: 特性メモ
  39: 11.875, // AM: 面談評価
  40: 11.875, // AN: 家族構成
  41: 26, // AO: 希望職種（分野）
  42: 14.625, // AP: 希望勤務地
  43: 16.75, // AQ: 希望手取り額
  44: 17.5, // AR: 転職可能時期
  45: 14.5, // AS: その他希望
  46: 10.875, // AT: 社内メモ
  47: 11.625, // AU: 面談日
  48: 30, // AV: 資格
  49: 40, // AW: 学歴
  50: 22, // AX: パートナー名
};

const headers: string[] = [
  "求職活動ステータス", //recruitmentStatus
  "紹介事業所名", //matchOfficeName
  "人材担当者", //personalInCharge
  "氏名", //fullName
  "カナ表記", //fullNameKana
  "生年月日", //birthday
  "呼び名", //nickName
  "人材管理番号", //talentNumber
  "国籍", //nationality
  "性別", //gender
  "電話番号", //phoneNumber
  "メールアドレス", //email
  "その他の連絡手段・SNS①", //contactType
  "その他の連絡先ID等①", //contactInfo
  "その他の連絡手段・SNS②", //contactType2
  "その他の連絡先ID等②", //contactInfo2
  "その他の連絡手段・SNS③", //contactType3
  "その他の連絡先ID等③", //contactInfo3
  "その他の連絡手段・SNS④", //contactType4
  "その他の連絡先ID等④", //contactInfo4
  "居住地", //location
  "郵便番号", //zipCode
  "所在地（町名丁目番地号等）", //address
  "本国における居住地", //country
  "在留資格", //residenceStatus
  "在留期限", //periodOfStay
  "特定技能1号起算日", //specifiedSkillFirstStartDate
  "ブランク期間（月単位）", //specifiedSkillFirstRemainingTerm
  "パスポート番号", //passportNumber
  "パスポート期限", //passportExpiryDate
  "分野", //specificBusinessClassificationFirst
  "区分", //specificBusinessClassificationTypeFirst
  "日本語能力証明書等（実習修了証明含む）", //languageCertificates
  "日本語能力（その他）", //languageTestOther
  "特定技能合格証", //specificSkillCertificates
  "職種", //majorName
  "作業", //jobName
  "特性メモ", //memo
  "面談評価", //interviewEvaluation
  "家族構成", //familyInformation
  "希望職種（分野）", //desiredJobType
  "希望勤務地", //desiredWorkLocation
  "希望手取り額", //expectSalary
  "転職可能時期", //availableFrom
  "その他希望", //otherRequest
  "社内メモ", //internalMemo
  "面談日", //interviewDate
  "資格", //heldQualifications
  "学歴", //educationBackground
  "パートナー名", //partnerName
];

type TDownloadExcelOptions = {
  listIndustry?: TOption[];
  listResidenceStatus?: TOption[];
  listRecruitmentStatus?: TOption[];
  listMatchOffice?: TOption[];
  listPersonInCharge?: TOption[];
  listEducationLevel?: TOption[];
  customName?: string;
};

const handleDownloadExcel = async (data: string[][], options: TDownloadExcelOptions = {}) => {
  const {
    listIndustry = [],
    listResidenceStatus = [],
    listRecruitmentStatus = [],
    listMatchOffice = [],
    listPersonInCharge = [],
    listEducationLevel = [],
    customName = "人材情報_サンプル.xlsx",
  } = options;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("人材情報");

  // Master sheet: A=industry, B=residenceStatus, C=matchOffice, D=personInCharge, E=educationLevel
  const masterSheet = workbook.addWorksheet("Master");
  const industryCount = listIndustry.length;
  const residenceStatusCount = listResidenceStatus.length;
  const matchOfficeCount = listMatchOffice.length;
  const personInChargeCount = listPersonInCharge.length;
  const educationLevelCount = listEducationLevel.length;

  listIndustry.forEach((item, idx) => {
    masterSheet.getCell("A" + (idx + 1)).value = item.label;
  });

  listResidenceStatus.forEach((item, idx) => {
    masterSheet.getCell("B" + (idx + 1)).value = item.label;
  });

  listMatchOffice.forEach((item, idx) => {
    masterSheet.getCell("C" + (idx + 1)).value = item.label;
  });

  listPersonInCharge.forEach((item, idx) => {
    masterSheet.getCell("D" + (idx + 1)).value = item.label;
  });

  listEducationLevel.forEach((item, idx) => {
    masterSheet.getCell("E" + (idx + 1)).value = item.label;
  });

  masterSheet.state = "veryHidden";

  const headerRow = sheet.addRow(headers);

  for (const row of data) {
    sheet.addRow(row);
  }

  headerRow.eachCell((cell, colNumber) => {
    cell.font = { name: "Calibri", bold: true, size: 14, color: { argb: "FFFFFFFF" } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4472C4" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    const column = sheet.getColumn(colNumber);
    column.width = columnWidths[colNumber] || 15;
  });

  sheet.views = [{ state: "frozen", ySplit: 1 }];

  for (let i = 2; i <= TEMPLATE_END_ROW; i++) {
    // Master data dropdowns (from hidden Master sheet)
    if (industryCount > 0) {
      sheet.getCell(`AE${i}`).dataValidation = {
        type: "list",
        formulae: [`=Master!$A$1:$A$${industryCount}`],
        showInputMessage: true,
      };
    }

    if (residenceStatusCount > 0) {
      sheet.getCell(`Y${i}`).dataValidation = {
        type: "list",
        formulae: [`=Master!$B$1:$B$${residenceStatusCount}`],
        showInputMessage: true,
      };
    }

    if (matchOfficeCount > 0) {
      sheet.getCell(`B${i}`).dataValidation = {
        type: "list",
        formulae: [`=Master!$C$1:$C$${matchOfficeCount}`],
        showInputMessage: true,
      };
    }

    if (personInChargeCount > 0) {
      sheet.getCell(`C${i}`).dataValidation = {
        type: "list",
        formulae: [`=Master!$D$1:$D$${personInChargeCount}`],
        showInputMessage: true,
      };
    }

    // 学歴 (col AW) — dropdown from EducationLevelOptions (Master col E), like 人材担当者
    if (educationLevelCount > 0) {
      sheet.getCell(`AW${i}`).dataValidation = {
        type: "list",
        formulae: [`=Master!$E$1:$E$${educationLevelCount}`],
        showInputMessage: true,
      };
    }

    // Enum dropdowns
    const recruitmentStatus = listRecruitmentStatus.map((option) => option.label).join(",");

    sheet.getCell(`A${i}`).dataValidation = {
      type: "list",
      formulae: [`"${recruitmentStatus}"`],
      showInputMessage: true,
    };

    sheet.getCell(`J${i}`).dataValidation = {
      type: "list",
      formulae: ['"男性,女性"'],
      showInputMessage: true,
    };

    // Contact type validation for all 4 columns (M, O, Q, S)
    for (const col of ["M", "O", "Q", "S"]) {
      sheet.getCell(`${col}${i}`).dataValidation = {
        type: "list",
        formulae: ['"Messenger,LINE,Zalo,FB"'],
        showInputMessage: true,
      };
    }

    sheet.getCell(`U${i}`).dataValidation = {
      type: "list",
      formulae: ['"日本,日本以外"'],
      showInputMessage: true,
    };

    sheet.getCell(`F${i}`).numFmt = "yyyy/mm/dd";
    sheet.getCell(`Z${i}`).numFmt = "yyyy/mm/dd";
    sheet.getCell(`AD${i}`).numFmt = "yyyy/mm/dd";
    sheet.getCell(`AU${i}`).numFmt = "yyyy/mm/dd";
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, customName);
};

type TDownloadSampleOptions = {
  industry?: TOption[];
  residenceStatus?: TOption[];
  recruitmentStatus?: TOption[];
  matchOffice?: TOption[];
  personInCharge?: TOption[];
  educationLevel?: TOption[];
};

export const downloadSample = async (options: TDownloadSampleOptions = {}) => {
  const { industry = [], residenceStatus = [], recruitmentStatus = [], matchOffice = [], personInCharge = [], educationLevel = [] } = options;

  const data = [
    "求職中",
    matchOffice.length > 0 ? matchOffice[0].label : "東京支店",
    personInCharge.length > 0 ? personInCharge[0].label : "矢野裕子",
    "NGUYEN THI PHUONG",
    "グエン ティ フォン",
    "2003/03/03",
    "フォン",
    "00197_工べ",
    "ベトナム",
    "女性",
    "090-1234-5678",
    "nguyenthiphuong@gmail.com",
    "LINE",
    "username-ps-armada",
    "", // contactType2
    "", // contactInfo2
    "", // contactType3
    "", // contactInfo3
    "", // contactType4
    "", // contactInfo4
    "日本",
    "160-0023",
    "3-3-13 西新宿水間ビル６階",
    "ベトナム　HAU　GIANG",
    "特定技能1号",
    "2027/04/04",
    "2021/05/05",
    "10",
    "C1111111",
    "2026/11/11",
    "工業製品製造業",
    "機械金属加工|電気電子機器組立て",
    "日本語能力試験・N4|日本語能力試験・N3",
    "BJTビジネス日本語能力テスト",
    "介護技能評価試験の合格証明書|技能検定1級の合格証明書",
    "さく井",
    "パーカッション式さく井工事",
    "",
    "",
    "",
    "介護|飲食料品製造業",
    "東京",
    "20万円以上",
    "来月",
    "",
    "",
    "2026/10/10",
    "介護福祉士|普通自動車免許", // 資格 (heldQualifications)
    "中卒|高卒", // 学歴 (educationBackground)
    "", // パートナー名 (partnerName)
  ];
  const listIndustries = industry.filter((item) => !item.hidden);
  const listResidenceStatuses = residenceStatus.filter((item) => !item.hidden);

  await handleDownloadExcel([data], {
    listIndustry: listIndustries,
    listResidenceStatus: listResidenceStatuses,
    listRecruitmentStatus: recruitmentStatus,
    listMatchOffice: matchOffice,
    listPersonInCharge: personInCharge,
    listEducationLevel: educationLevel,
    customName: "人材情報_サンプル.xlsx",
  });
};

export const exportExcel = async (listData: string[][], listIndustry: TOption[] = [], matchName: string) => {
  await handleDownloadExcel(listData, {
    listIndustry,
    customName: `${matchName}_人材DB_${dayjs().unix()}.xlsx`,
  });
};

// Normalize alternative contact header names to canonical format
const normalizeHeader = (header: string): string => {
  if (header === "その他の連絡手段、SNS") return "その他の連絡手段・SNS①";
  if (header === "その他の連絡先ID等") return "その他の連絡先ID等①";
  const regularToCircled: Record<string, string> = { "1": "①", "2": "②", "3": "③", "4": "④" };
  header = header.replace(/その他の連絡手段(\d)/g, (_, n) => `その他の連絡手段・SNS${regularToCircled[n] || n}`);
  header = header.replace(/その他の連絡先ID等(\d)/g, (_, n) => `その他の連絡先ID等${regularToCircled[n] || n}`);
  return header;
};

export const validateFileFormat = (rowHeader: string[]) => {
  const header = rowHeader
    .filter((h) => h !== "")
    .map((h) =>
      normalizeHeader(
        String(h)
          .replace(/^\uFEFF/, "")
          .trim(),
      ),
    );

  if (header.length === 4) {
    const validHeaderUpdate = ["氏名", "生年月日", "人材管理番号", "ファイルデータ"];
    if (header.length === validHeaderUpdate.length && header.every((value, index) => value === validHeaderUpdate[index])) {
      return {
        isValid: true,
        isUpdate: true,
      };
    } else {
      throw new Error(FILE_INCORRECT);
    }
  }

  const requiredBefore = headers.slice(0, 14);
  const optionalContact = headers.slice(14, 20);
  // 居住地 … 面談日 stay required. 資格/学歴/パートナー名 (indices 47-49) are an optional tail
  // so old files without them still validate.
  const requiredAfter = headers.slice(20, 47);
  const minRequired = requiredBefore.length + requiredAfter.length;

  if (header.length < minRequired) {
    throw new Error(FILE_INCORRECT);
  }

  for (let i = 0; i < requiredBefore.length; i++) {
    if (header[i] !== requiredBefore[i]) {
      throw new Error(FILE_INCORRECT);
    }
  }

  let optionalCount = 0;
  for (let i = 0; i < optionalContact.length; i++) {
    if (header[requiredBefore.length + i] === optionalContact[i]) {
      optionalCount++;
    } else {
      break;
    }
  }

  // Validate required headers after contact fields (居住地 onwards)
  const afterStart = requiredBefore.length + optionalCount;
  for (let i = 0; i < requiredAfter.length; i++) {
    if (afterStart + i >= header.length || header[afterStart + i] !== requiredAfter[i]) {
      throw new Error(FILE_INCORRECT);
    }
  }

  return {
    isValid: true,
    isUpdate: false,
  };
};
